/**
 * Route Permissions Configuration
 * Defines which routes require which module rights
 * Used by the navigation guard to enforce access control
 */

// Routes protected by module rights
// Each module key maps to an array of route names that require that module's permission
export const moduleRoutes = {
  carbon: [
    'home',
    'projects',
    'site',
    'category',
    'emission-source',
    'add-emission-source',
    'add-emission-source-with-selected-subcategory',
    'board',
    'compare',
    'cartography',
    'export',
    'import-greenly',
    'import-zei'
  ],
  lca: [
    'lca',
    'lca-detail',
    'lca-epd'
  ],
  trajectory: [
    'action-plans',
    'projected',
    'trajectory-settings',
    'actions',
    'action-plan',
    'add-action-plan'
  ],
  zelia: [
    'zelia',
    'zelia-site',
    'rules'
  ],
  form: [
    'forms',
    'formCreate',
    'formEdit',
    'formResponse'
  ]
}

// Public routes - accessible without authentication
export const publicRoutes = [
  'login',
  'lost-password',
  'password-email-sent',
  'new-password',
  'register-from-invitation',
  'policy',
  'not-found',
  'no-access',
  'respondByToken', // QUEST-4.8: Accès public pour répondants aux questionnaires (mode token)
  'formResponse', // QUEST-4.8: Accès public pour répondants aux questionnaires (mode public_id)
  'formJoin', // SHARE-1.2: Écran d'accueil du lien de partage public
  'formErase', // SHARE-2.4: Demande de suppression des données personnelles
  'magic-login' // MAGIC-1.1: Connexion directe par lien permanent
]

// Routes accessible to all authenticated users (no specific module right required)
export const authenticatedRoutes = [
  'progress',
  'contact',
  'contact-confirmation'
]

// Routes that require admin status (isAdmin) but no specific module right
export const adminOnlyRoutes = [
  'parameters',
  'directory',
  'library',
  'factors-library',
  'factorsPage',
  'action-plans-library',
  'employees'
]

// Module priority order for redirection (first available module is used)
export const modulePriority = ['carbon', 'lca', 'trajectory', 'zelia', 'form']

// Default route for each module (used when redirecting to first authorized route)
export const moduleDefaultRoute = {
  carbon: '/',
  lca: '/lca',
  trajectory: '/action-plans',
  zelia: '/zelia',
  form: '/forms'
}

/**
 * Get the first authorized route based on user's module rights
 * @returns {string} The path to redirect to
 */
export function getFirstAuthorizedRoute() {
  const rightsStr = localStorage.getItem('companyRights')
  // If localStorage is corrupted or empty, allow all access to avoid blocking users
  if (!rightsStr) {
    return '/'
  }

  try {
    const rights = JSON.parse(rightsStr)

    for (const module of modulePriority) {
      // Check if user has this module right (default to true if not explicitly false)
      if (rights[module] !== false) {
        return moduleDefaultRoute[module]
      }
    }

    // No module rights at all - redirect to no-access page
    return '/no-access'
  } catch (e) {
    // JSON parse error - allow all access to avoid blocking users
    return '/'
  }
}

/**
 * Check if user has a specific module right
 * @param {string} moduleName - The module to check (carbon, lca, trajectory, zelia, form)
 * @returns {boolean} True if user has the module right
 */
export function hasModuleRight(moduleName) {
  const rightsStr = localStorage.getItem('companyRights')
  if (!rightsStr) {
    return true // Default to true if no rights configured
  }

  try {
    const rights = JSON.parse(rightsStr)
    return rights[moduleName] !== false
  } catch (e) {
    return true // Default to true on parse error
  }
}

/**
 * Get the module that a route requires
 * @param {string} routeName - The name of the route
 * @returns {string|null} The module name or null if route is public/admin-only
 */
export function getRequiredModule(routeName) {
  for (const [module, routes] of Object.entries(moduleRoutes)) {
    if (routes.includes(routeName)) {
      return module
    }
  }
  return null // Route is public or admin-only
}

/**
 * Check if a route is public (accessible without authentication)
 * @param {string} routeName - The name of the route
 * @returns {boolean} True if route is public
 */
export function isPublicRoute(routeName) {
  return publicRoutes.includes(routeName)
}

/**
 * Check if a route is accessible to all authenticated users
 * @param {string} routeName - The name of the route
 * @returns {boolean} True if route is accessible to authenticated users
 */
export function isAuthenticatedRoute(routeName) {
  return authenticatedRoutes.includes(routeName)
}

/**
 * Check if a route requires admin status
 * @param {string} routeName - The name of the route
 * @returns {boolean} True if route requires admin
 */
export function isAdminOnlyRoute(routeName) {
  return adminOnlyRoutes.includes(routeName)
}
